import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { toast } from 'sonner';
import {
    Edit2, Ban, RotateCcw, Trash2, MessageSquare,
    User, Mail, Phone, MapPin, Shield, ShieldOff, Bell, BellOff,
    Upload, CheckCircle2, FileText, Download, AlertTriangle, Plus,
    Camera, Pencil, Check, X
} from 'lucide-react';

import { useApi } from '../../lib/api.js';
import { useCurrentUser } from '../../hooks/useCurrentUser.js';
import { PageHeader, LoadingPage, ErrorPage, StatusPill } from '../../components/ui/Feedback.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { Textarea } from '../../components/ui/Form.jsx';
import { DocumentUploadModal } from '../../components/DocumentUploadModal.jsx';
import { cn } from '../../lib/cn.js';
import { SectionDivider } from '../../components/ui/SectionDivider.jsx';
import { computeDisplayStatus } from '../../lib/computeStatus.js';

// ...near other small components in the file:

/**
 * Status pill that shows the displayed status plus collapsed reasons.
 * On hover or tap, the reasons surface as a tooltip-style line below.
 * Compact when active, expanded when inactive with reasons to communicate.
 */
function StatusWithReasons({ entity }) {
    const { tone, label, reasons } = computeDisplayStatus(entity);

    if (reasons.length === 0) {
        return <StatusPill status={tone}>{label}</StatusPill>;
    }

    return (
        <div className="flex flex-col items-end gap-1">
            <StatusPill status={tone}>{label}</StatusPill>
            <div className="text-xs text-ink-subtle text-right">
                {reasons.join(' · ')}
            </div>
        </div>
    );
}

// ============================================================================
// Required document slots for a vehicle
// ============================================================================
const REQUIRED_VEHICLE_DOCS = [
    { type: 'mot', label: 'MOT Certificate', requiresExpiry: true },
    { type: 'insurance', label: 'Insurance', requiresExpiry: true },
    { type: 'phv_licence', label: 'PHV Licence', requiresExpiry: true },
    { type: 'v5', label: 'V5 Logbook', requiresExpiry: false },
];

// Five canonical vehicle photo angles. All optional — shown for catalogue /
// partner verification. Photos are upload-only here; in Round 7e the partner
// portal provides the same five slots for partners to self-serve.
const VEHICLE_PHOTO_SLOTS = [
    { type: 'photo_front_exterior', label: 'Front (exterior)' },
    { type: 'photo_side_exterior',  label: 'Side (exterior)' },
    { type: 'photo_rear_exterior',  label: 'Rear (exterior)' },
    { type: 'photo_front_interior', label: 'Front (interior)' },
    { type: 'photo_rear_interior',  label: 'Rear (interior)' },
];
const PHOTO_TYPES = new Set(VEHICLE_PHOTO_SLOTS.map(p => p.type));

const CLASS_LABELS = {
    business: 'Business',
    first: 'First',
    suv: 'SUV',
    xl: 'XL',
};

const CLASS_STYLES = {
    business: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200',
    first:    'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200',
    suv:      'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200',
    xl:       'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200',
};

export function VehicleDetailPage() {
    const { id } = useParams();
    const api = useApi();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { isAdmin, isOwner } = useCurrentUser();
    const { getToken } = useAuth();

    const [uploadingDoc, setUploadingDoc] = useState(null);
    const [showAdditionalDocModal, setShowAdditionalDocModal] = useState(false);
    const [previewDoc, setPreviewDoc] = useState(null);
    const [showDisableModal, setShowDisableModal] = useState(false);
    const [showEnableModal, setShowEnableModal] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);

    const vehicleQuery = useQuery({
        queryKey: ['vehicle', id],
        queryFn: () => api.get(`/vehicles/${id}`),
    });

    const documentsQuery = useQuery({
        queryKey: ['documents', 'vehicle', id],
        queryFn: () => api.get(`/vehicles/${id}/documents`),
        enabled: Boolean(id),
    });

    const pairingQuery = useQuery({
        queryKey: ['vehicle', id, 'pairing'],
        queryFn: () => api.get(`/vehicles/${id}/pairing`),
        enabled: Boolean(id),
    });

    const notesQuery = useQuery({
        queryKey: ['notes', 'vehicle', id],
        queryFn: () => api.get(`/vehicles/${id}/notes`),
        enabled: Boolean(id),
    });

    const waiveMutation = useMutation({
        mutationFn: ({ doc_type, waived }) => api.post(`/vehicles/${id}/document-waiver`, { doc_type, waived }),
        onSuccess: (_data, vars) => {
            queryClient.invalidateQueries({ queryKey: ['vehicle', id] });
            toast.success(vars.waived ? 'Marked not required' : 'Marked required');
        },
        onError: (err) => toast.error(err.message || 'Could not update'),
    });

    // Download Download profile PDF — bypasses useApi because the api wrapper
    // assumes JSON responses and corrupts binary PDFs. The /api path is
    // proxied by Vercel to the Railway backend (matches useApi's API_BASE).
    async function handleDownloadCompliancePdf() {
        if (!id) return;
        setDownloadingPdf(true);
        try {
            const token = await getToken();
            if (!token) throw new Error('Not authenticated');

            const response = await fetch(`/api/vehicles/${id}/compliance-pdf`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (!response.ok) {
                // Try to read a JSON error body if the server returned one
                let detail = '';
                try {
                    const body = await response.json();
                    detail = body?.error || body?.message || '';
                } catch (_) {
                    // Non-JSON error response, ignore
                }
                throw new Error(detail || `Download profile PDF generation failed (${response.status})`);
            }

            const blob = await response.blob();

            // Extract filename from Content-Disposition; fall back to a sensible default.
            const disposition = response.headers.get('content-disposition') || '';
            const match = disposition.match(/filename="?([^";]+)"?/i);
            const reg = (vehicleQuery.data?.registration || id).toUpperCase().replace(/[^A-Z0-9]/g, '');
            const fallbackName = `Lanz-CTS-Vehicle-${reg}.pdf`;
            const filename = (match && match[1]) || fallbackName;

            const objectUrl = window.URL.createObjectURL(blob);
            const link = window.document.createElement('a');
            link.href = objectUrl;
            link.download = filename;
            window.document.body.appendChild(link);
            link.click();
            window.document.body.removeChild(link);
            window.URL.revokeObjectURL(objectUrl);
        } catch (err) {
            console.error('Download profile PDF download failed:', err);
            toast.error(err.message || 'Failed to download Download profile PDF');
        } finally {
            setDownloadingPdf(false);
        }
    }

    if (vehicleQuery.isLoading) return <LoadingPage />;
    if (vehicleQuery.isError) return <ErrorPage message={vehicleQuery.error?.message} onRetry={vehicleQuery.refetch} />;

    const vehicle = vehicleQuery.data;
    const documents = documentsQuery.data?.documents || [];
    const pairedChauffeur = pairingQuery.data;
    const notes = notesQuery.data?.notes || [];

    const requiredTypes = new Set(REQUIRED_VEHICLE_DOCS.map(d => d.type));
        // One doc per type: approved beats pending beats rejected; newest wins
    // within the same status.
    const pickDocsByType = (typesSet) => {
        const rank = (d) => (!d.approval_status || d.approval_status === 'approved') ? 0
            : d.approval_status === 'pending' ? 1 : 2;
        const map = new Map();
        const sorted = documents
            .filter(d => typesSet.has(d.doc_type))
            .sort((a, b) => rank(a) - rank(b) || new Date(b.uploaded_at) - new Date(a.uploaded_at));
        for (const d of sorted) if (!map.has(d.doc_type)) map.set(d.doc_type, d);
        return map;
    };
    const requiredDocsByType = pickDocsByType(requiredTypes);
    const waivedTypes = new Set(vehicle.waived_doc_types || []);
        const photosByType = pickDocsByType(PHOTO_TYPES);
    // Additional = not required, not photos, not 'other' system types — user-added extras only
    const additionalDocs = documents.filter(d =>
        d.doc_type === 'other'
    );

    const isActive = vehicle.status === 'active';

    // Header actions: Download profile PDF (left) + Edit (right), matching chauffeur detail page pattern.
    // PDF button visible to anyone who can view the vehicle — it's a read operation.
    // Edit button is admin-only.
    const actions = (
        <div className="flex gap-2">
            <Button
                variant="secondary"
                onClick={handleDownloadCompliancePdf}
                loading={downloadingPdf}
                disabled={downloadingPdf}
            >
                <FileText className="h-4 w-4" />
                <span className="hidden sm:inline">Download profile PDF</span>
            </Button>
            {isAdmin && (
                <Button variant="secondary" onClick={() => navigate(`/vehicles/${id}/edit`)}>
                    <Edit2 className="h-4 w-4" />
                    <span className="hidden sm:inline">Edit</span>
                </Button>
            )}
        </div>
    );

    return (
        <div>
            <PageHeader
                title={
                    <span className="inline-flex items-center gap-3 flex-wrap">
                        <span className="inline-flex items-center px-2 py-1 rounded bg-ink text-paper font-mono text-sm font-medium tracking-wide">
                            {vehicle.registration}
                        </span>
                        <span>{vehicle.make} {vehicle.model}</span>
                    </span>
                }
                subtitle={
                    <span className="inline-flex items-center gap-2 flex-wrap">
                        {vehicle.nickname && (
                            <span className="text-ink">{vehicle.nickname}</span>
                        )}
                       <span className={cn('inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium',
                            CLASS_STYLES[vehicle.vehicle_class] || 'bg-paper-hover text-ink-muted')}>
                            {CLASS_LABELS[vehicle.vehicle_class] || vehicle.vehicle_class}
                        </span>
                        {vehicle.fleet_partner_id && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
                                {vehicle.fleet_partner_company_name || 'Operator'}
                            </span>
                        )}
                        <StatusWithReasons entity={vehicle} />
                    </span>
                }
                backTo="/vehicles"
                backLabel="Vehicles"
                actions={actions}
            />

            {!isActive && (
                <div className="card mb-5 border-amber-200 bg-amber-50">
                    <div className="card-body flex items-start gap-3">
                        <AlertTriangle className="h-4 w-4 text-status-warning flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                            <div className="text-sm font-medium text-status-warning">
                                This vehicle is inactive and cannot be assigned to new jobs.
                            </div>
                            {vehicle.disabled_reason && (
                                <div className="text-xs text-status-warning/80 mt-1">
                                    {vehicle.disabled_reason}
                                </div>
                            )}
                            {vehicle.disabled_at && (
                                <div className="text-2xs text-status-warning/70 mt-1">
                                    Disabled {formatDate(vehicle.disabled_at)}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Left column */}
                <div className="lg:col-span-1 space-y-5">
                    <ProfileCard vehicle={vehicle} />
                    {/* 7d.5.5b — Licensing & Registered keeper merged into ProfileCard; components kept for now in case we revert */}
                    {/* <LicensingCard vehicle={vehicle} /> */}
                    {/* <RegisteredKeeperCard vehicle={vehicle} /> */}
                    <PairedChauffeurCard pairing={pairedChauffeur} onNavigate={navigate} />

                    {isOwner && isActive && (
                        <div className="card">
                            <div className="card-body">
                                <Button
                                    variant="secondary"
                                    className="w-full text-status-danger hover:text-status-danger"
                                    onClick={() => setShowDisableModal(true)}
                                >
                                    <Ban className="h-4 w-4" />
                                    Disable vehicle
                                </Button>
                            </div>
                        </div>
                    )}
                    {isOwner && !isActive && (
                        <div className="card">
                            <div className="card-body">
                                <Button
                                    variant="secondary"
                                    className="w-full"
                                    onClick={() => setShowEnableModal(true)}
                                >
                                    <RotateCcw className="h-4 w-4" />
                                    Re-enable vehicle
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right column */}
               <div className="lg:col-span-2 space-y-5">
                    {vehicle.fleet_partner_id ? (
                        <div className="card">
                            <div className="card-header">
                                <h2 className="text-sm font-semibold text-ink">Documents</h2>
                                <p className="text-xs text-ink-subtle mt-0.5">
                                    Managed by the operator. This vehicle runs on their operator licence and is excluded from Lanz CTS compliance reports.
                                </p>
                            </div>
                            <div className="card-body">
                                <p className="text-sm text-ink-subtle">
                                    No required documents for operator vehicles. Use Additional documents below to keep anything on file.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <RequiredDocumentsCard
                            docsByType={requiredDocsByType}
                            isAdmin={isAdmin}
                            waivedTypes={waivedTypes}
                            onWaive={isOwner ? (doc_type, waived) => waiveMutation.mutate({ doc_type, waived }) : null}
                            onUpload={(docConfig) => setUploadingDoc(docConfig)}
                            onPreview={(doc) => setPreviewDoc(doc)}
                        />
                    )}
                    <VehiclePhotosCard
                        photosByType={photosByType}
                        isAdmin={isAdmin}
                        onUpload={(docConfig) => setUploadingDoc(docConfig)}
                        onPreview={(doc) => setPreviewDoc(doc)}
                    />
                    <AdditionalDocumentsCard
                        docs={additionalDocs}
                        isAdmin={isAdmin}
                        onUpload={() => setShowAdditionalDocModal(true)}
                        onPreview={(doc) => setPreviewDoc(doc)}
                    />
                    <NotesCard
                        vehicleId={id}
                        notes={notes}
                        isLoading={notesQuery.isLoading}
                        isOwner={isOwner}
                    />
                </div>
            </div>

            {/* Modals */}
            {uploadingDoc && (
                <DocumentUploadModal
                    ownerType="vehicle"
                    ownerId={id}
                    docType={uploadingDoc.type}
                    docLabel={uploadingDoc.label}
                    requiresExpiry={uploadingDoc.requiresExpiry || false}
                    open
                    onClose={() => setUploadingDoc(null)}
                />
            )}

            {showAdditionalDocModal && (
                <DocumentUploadModal
                    ownerType="vehicle"
                    ownerId={id}
                    docType="other"
                    docLabel="additional document"
                    requiresExpiry
                    expiryOptional
                    open
                    onClose={() => setShowAdditionalDocModal(false)}
                />
            )}

            {showDisableModal && (
                <DisableModal
                    vehicleId={id}
                    vehicleDescription={`${[vehicle.make, vehicle.model].filter(Boolean).join(' ') || 'Vehicle'} (${vehicle.registration})`}
                    open
                    onClose={() => setShowDisableModal(false)}
                    onDisabled={() => {
                        setShowDisableModal(false);
                        vehicleQuery.refetch();
                        queryClient.invalidateQueries({ queryKey: ['vehicles'] });
                    }}
                />
            )}

            {showEnableModal && (
                <EnableModal
                    vehicleId={id}
                    vehicleDescription={`${[vehicle.make, vehicle.model].filter(Boolean).join(' ') || 'Vehicle'} (${vehicle.registration})`}
                    open
                    onClose={() => setShowEnableModal(false)}
                    onEnabled={() => {
                        setShowEnableModal(false);
                        vehicleQuery.refetch();
                        queryClient.invalidateQueries({ queryKey: ['vehicles'] });
                    }}
                />
            )}

           {previewDoc && (
                <DocumentPreviewModal
                    document={previewDoc}
                    open={Boolean(previewDoc)}
                    onClose={() => setPreviewDoc(null)}
                    ownerId={id}
                    isOwner={isOwner}
                />
            )}
        </div>
    );
}

// ============================================================================
// Profile card (VIN removed; nickname moved to subtitle as customer descriptor)
// ============================================================================
function ProfileCard({ vehicle }) {
    const rows = [
        { label: 'Registration', value: <span className="font-mono">{vehicle.registration}</span> },
        { label: 'Make & model', value: [vehicle.make, vehicle.model].filter(Boolean).join(' ') },
        ...(vehicle.year ? [{ label: 'Year', value: vehicle.year }] : []),
        ...(vehicle.colour ? [{ label: 'Colour', value: vehicle.colour }] : []),
        ...(vehicle.nickname ? [{ label: 'Description', value: vehicle.nickname, hint: 'Shown on customer confirmations' }] : []),
        { label: 'Class', value: CLASS_LABELS[vehicle.vehicle_class] || vehicle.vehicle_class },
        { label: 'Seats', value: vehicle.seats },
        ...(vehicle.luggage_capacity != null ? [{ label: 'Luggage', value: `${vehicle.luggage_capacity} (guide)` }] : []),
    ];

    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Profile</h2>
            </div>
            <div className="card-body">
              <dl className="space-y-3">
    {/* Identity rows */}
    {rows.map(({ label, value }) => (
        <div key={label}>
            <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">{label}</dt>
            <dd className="text-sm text-ink mt-0.5 break-words">{value || '—'}</dd>
        </div>
    ))}

    {/* Licensing — sub-grouped, matches chauffeur ProfileCard order */}
    <SectionDivider label="Licensing" />
    <div>
        <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Authority</dt>
        <dd className="text-sm text-ink mt-0.5 break-words">{vehicle.licensing_authority || '—'}</dd>
    </div>
    <div>
        <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">PHV Licence No.</dt>
        <dd className="text-sm text-ink mt-0.5 break-words">{vehicle.licence_plate_number || '—'}</dd>
    </div>
    {vehicle.licence_plate_expires_at && (
        <div>
            <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Licence expires</dt>
            <dd className="text-sm text-ink mt-0.5 break-words">{formatDate(vehicle.licence_plate_expires_at)}</dd>
        </div>
    )}

    {/* Engagement — start/end dates of vehicle in fleet (matches chauffeur ProfileCard order) */}
    {(vehicle.start_date || vehicle.end_date) && (
        <>
            <SectionDivider label="Engagement" />
            {vehicle.start_date && (
                <div>
                    <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Started</dt>
                    <dd className="text-sm text-ink mt-0.5 break-words">{formatDate(vehicle.start_date)}</dd>
                </div>
            )}
            {vehicle.end_date && (
                <div>
                    <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Ended</dt>
                    <dd className="text-sm text-ink mt-0.5 break-words">{formatDate(vehicle.end_date)}</dd>
                </div>
            )}
        </>
    )}

    {/* Registered keeper (V5) — only render if at least one keeper field is set */}
    {(vehicle.registered_keeper_name
      || vehicle.registered_keeper_email
      || vehicle.registered_keeper_phone
      || vehicle.registered_keeper_address) && (
        <>
            <SectionDivider label="Registered keeper (V5)" />
            {vehicle.registered_keeper_name && (
                <div>
                    <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Name</dt>
                    <dd className="text-sm text-ink mt-0.5 break-words">{vehicle.registered_keeper_name}</dd>
                </div>
            )}
            {vehicle.registered_keeper_email && (
                <div>
                    <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Email</dt>
                    <dd className="text-sm text-ink mt-0.5 break-words">
                        <a href={`mailto:${vehicle.registered_keeper_email}`} className="text-ink hover:underline">
                            {vehicle.registered_keeper_email}
                        </a>
                    </dd>
                </div>
            )}
            {vehicle.registered_keeper_phone && (
                <div>
                    <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Phone</dt>
                    <dd className="text-sm text-ink mt-0.5 break-words">
                        <a href={`tel:${vehicle.registered_keeper_phone}`} className="text-ink hover:underline">
                            {vehicle.registered_keeper_phone}
                        </a>
                    </dd>
                </div>
            )}
            {vehicle.registered_keeper_address && (
                <div>
                    <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Address</dt>
                    <dd className="text-sm text-ink mt-0.5 break-words whitespace-pre-line">{vehicle.registered_keeper_address}</dd>
                </div>
            )}
        </>
    )}
</dl>
            </div>
        </div>
    );
}

// ============================================================================
// Licensing card (PHV plate number -> PHV licence number)
// ============================================================================
function LicensingCard({ vehicle }) {
    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Licensing</h2>
            </div>
            <div className="card-body">
                <dl className="space-y-3">
                    <div className="flex items-start gap-3">
                        <Shield className="h-4 w-4 text-ink-subtle mt-0.5 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                            <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Authority</dt>
                            <dd className="text-sm text-ink mt-0.5">{vehicle.licensing_authority}</dd>
                        </div>
                    </div>
                    {vehicle.licence_plate_number && (
                        <div>
                            <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">PHV licence</dt>
                            <dd className="text-sm text-ink mt-0.5 font-mono">{vehicle.licence_plate_number}</dd>
                        </div>
                    )}
                </dl>
               {vehicle.include_in_compliance === false && (
                    <div className="mt-4 pt-4 border-t border-paper-border-soft">
                        <div className="inline-flex items-center gap-1.5 text-xs text-ink-muted">
                            <ShieldOff className="h-3.5 w-3.5" />
                            <span>Excluded from reports</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ============================================================================
// Registered keeper card
// ============================================================================
function RegisteredKeeperCard({ vehicle }) {
    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Registered keeper</h2>
                <p className="text-xs text-ink-subtle mt-0.5">Receives document expiry reminders.</p>
            </div>
            <div className="card-body">
                <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-md bg-paper-hover flex items-center justify-center text-ink-muted flex-shrink-0">
                        <User className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-ink">{vehicle.registered_keeper_name}</div>
                        <div className="text-xs text-ink-muted mt-1.5 flex items-center gap-1.5">
                            <Mail className="h-3 w-3 flex-shrink-0" />
                            <a href={`mailto:${vehicle.registered_keeper_email}`} className="hover:text-ink truncate">
                                {vehicle.registered_keeper_email}
                            </a>
                        </div>
                        {vehicle.registered_keeper_phone && (
                            <div className="text-xs text-ink-muted mt-1 flex items-center gap-1.5">
                                <Phone className="h-3 w-3 flex-shrink-0" />
                                <a href={`tel:${vehicle.registered_keeper_phone}`} className="hover:text-ink">
                                    {vehicle.registered_keeper_phone}
                                </a>
                            </div>
                        )}
                        {vehicle.registered_keeper_address && (
                            <div className="text-xs text-ink-muted mt-1 flex items-start gap-1.5">
                                <MapPin className="h-3 w-3 flex-shrink-0 mt-0.5" />
                                <span>{vehicle.registered_keeper_address}</span>
                            </div>
                        )}
                    </div>
                </div>
                <div className="mt-4 pt-3 border-t border-paper-border-soft">
                    {vehicle.notify_keeper_on_expiry ? (
                        <div className="inline-flex items-center gap-1.5 text-xs text-ink-muted">
                            <Bell className="h-3.5 w-3.5" />
                            <span>Expiry reminders on</span>
                        </div>
                    ) : (
                        <div className="inline-flex items-center gap-1.5 text-xs text-ink-subtle">
                            <BellOff className="h-3.5 w-3.5" />
                            <span>Expiry reminders off</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ============================================================================
// Paired chauffeur card
// ============================================================================
function PairedChauffeurCard({ pairing, onNavigate }) {
    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Paired chauffeur</h2>
            </div>
            <div className="card-body">
                {pairing ? (
                    <button
                        type="button"
                        onClick={() => onNavigate(`/chauffeurs/${pairing.driver_id}`)}
                        className="w-full text-left flex items-start gap-3 -mx-2 -my-1 px-2 py-1 rounded hover:bg-paper-hover/50 transition-colors"
                    >
                        <div className="h-9 w-9 rounded-full bg-paper-hover flex items-center justify-center text-ink-muted flex-shrink-0">
                            <User className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-ink">
                                {pairing.first_name} {pairing.last_name}
                            </div>
                            {pairing.call_sign && (
                                <div className="mt-1">
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-ink text-paper font-mono text-2xs font-medium">
                                        {pairing.call_sign}
                                    </span>
                                </div>
                            )}
                            <div className="text-xs text-ink-subtle mt-1">
                                Paired {formatDate(pairing.created_at)}
                            </div>
                        </div>
                    </button>
                ) : (
                    <div>
                        <p className="text-sm text-ink-subtle">No chauffeur paired.</p>
                        <p className="text-xs text-ink-faint mt-1">Pairing is managed from the chauffeur side.</p>
                    </div>
                )}
            </div>
        </div>
    );
}

// ============================================================================
// Required documents card
// ============================================================================
function RequiredDocumentsCard({ docsByType, isAdmin, waivedTypes = new Set(), onWaive, onUpload, onPreview }) {
    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Required documents</h2>
                <p className="text-xs text-ink-subtle mt-0.5">
                    MOT, insurance, PHV licence, and V5 logbook.
                </p>
            </div>
            <div className="divide-y divide-paper-border-soft">
                {REQUIRED_VEHICLE_DOCS.map((docConfig) => {
                    const doc = docsByType.get(docConfig.type);
                    return (
                        <DocumentRow
                            key={docConfig.type}
                            label={docConfig.label}
                            requiresExpiry={docConfig.requiresExpiry}
                            document={doc}
                            isAdmin={isAdmin}
                            waived={waivedTypes.has(docConfig.type)}
                            onWaive={onWaive ? (w) => onWaive(docConfig.type, w) : null}
                            onUpload={() => onUpload(docConfig)}
                            onPreview={doc ? () => onPreview(doc) : null}
                        />
                    );
                })}
            </div>
        </div>
    );
}

// ============================================================================
// Vehicle photos card — 5-slot grid, all optional
// ============================================================================
function VehiclePhotosCard({ photosByType, isAdmin, onUpload, onPreview }) {
    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Vehicle photos</h2>
                <p className="text-xs text-ink-subtle mt-0.5">
                    Optional. Five-angle catalogue — useful for verifying operator vehicles.
                    Partners can also self-upload via the partner portal.
                </p>
            </div>
            <div className="card-body">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {VEHICLE_PHOTO_SLOTS.map((slot) => {
                        const photo = photosByType.get(slot.type);
                        return (
                            <PhotoTile
                                key={slot.type}
                                slot={slot}
                                photo={photo}
                                isAdmin={isAdmin}
                                onUpload={() => onUpload({
                                    type: slot.type,
                                    label: slot.label,
                                    requiresExpiry: false,
                                })}
                                onPreview={photo ? () => onPreview(photo) : null}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function PhotoTile({ slot, photo, isAdmin, onUpload, onPreview }) {
    const api = useApi();

    // Signed download URL for thumbnail (same pattern as ChauffeurAvatar)
    const { data: urlData } = useQuery({
        queryKey: ['document-download-url', photo?.id],
        queryFn: () => api.get(`/documents/${photo.id}/download-url`),
        enabled: Boolean(photo),
        staleTime: 30_000,
    });

    const thumbnailUrl = urlData?.download_url;

    return (
        <div className="group relative">
            <button
                type="button"
                onClick={photo && onPreview ? onPreview : (isAdmin ? onUpload : undefined)}
                disabled={!photo && !isAdmin}
                className={cn(
                    'aspect-[4/3] w-full rounded-md border overflow-hidden relative',
                    'flex flex-col items-center justify-center text-center p-2 transition-colors',
                    photo
                        ? 'border-paper-border bg-paper-hover cursor-pointer hover:border-ink-faint'
                        : (isAdmin
                            ? 'border-dashed border-paper-border bg-paper hover:border-ink-faint hover:bg-paper-hover/50 cursor-pointer'
                            : 'border-dashed border-paper-border bg-paper opacity-60')
                )}
            >
                {photo && thumbnailUrl ? (
                    <img
                        src={thumbnailUrl}
                        alt={slot.label}
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                ) : photo ? (
                    <div className="text-ink-subtle text-xs">Loading…</div>
                ) : (
                    <>
                        <Camera className="h-5 w-5 text-ink-subtle" />
                        <div className="mt-1.5 text-2xs text-ink-subtle font-medium">
                            {slot.label}
                        </div>
                    </>
                )}
            </button>
            {photo && (
                <div className="mt-1 flex items-center justify-between gap-2">
                    <span className="text-2xs text-ink-subtle truncate">{slot.label}</span>
                    {isAdmin && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onUpload(); }}
                            className="text-2xs text-ink-muted hover:text-ink transition-colors flex-shrink-0"
                        >
                            Replace
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

// ============================================================================
// Additional documents card
// ============================================================================
function AdditionalDocumentsCard({ docs, isAdmin, onUpload, onPreview }) {
    return (
        <div className="card">
            <div className="card-header flex items-center justify-between gap-4">
                <div>
                    <h2 className="text-sm font-semibold text-ink">Additional documents</h2>
                    <p className="text-xs text-ink-subtle mt-0.5">
                        Optional extra documents — service history, lease agreement, recall notices, etc.
                    </p>
                </div>
                {isAdmin && (
                    <Button size="sm" variant="secondary" onClick={onUpload}>
                        <Plus className="h-3.5 w-3.5" />
                        Add
                    </Button>
                )}
            </div>
            {docs.length === 0 ? (
                <div className="card-body">
                    <p className="text-sm text-ink-subtle text-center py-2">No additional documents.</p>
                </div>
            ) : (
                <div className="divide-y divide-paper-border-soft">
                    {docs.map((doc) => (
                        <DocumentRow
                            key={doc.id}
                            label={doc.custom_label || formatDocType(doc.doc_type)}
                            requiresExpiry={Boolean(doc.expires_at)}
                            document={doc}
                            isAdmin={isAdmin}
                            isAdditional
                            onPreview={() => onPreview(doc)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

// ============================================================================
// Notes card
// ============================================================================
function NotesCard({ vehicleId, notes, isLoading, isOwner }) {
    const [composerText, setComposerText] = useState('');
    const api = useApi();
    const queryClient = useQueryClient();

    const addNote = useMutation({
        mutationFn: () => api.post(`/vehicles/${vehicleId}/notes`, { note_text: composerText.trim() }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notes', 'vehicle', vehicleId] });
            setComposerText('');
            toast.success('Note added');
        },
        onError: (err) => toast.error(err.message || 'Could not add note'),
    });

    const deleteNote = useMutation({
        mutationFn: (noteId) => api.delete(`/vehicles/notes/${noteId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notes', 'vehicle', vehicleId] });
            toast.success('Note deleted');
        },
        onError: (err) => toast.error(err.message || 'Could not delete note'),
    });

    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Notes</h2>
                <p className="text-xs text-ink-subtle mt-0.5">
                    Timestamped notes — service history, incidents, reminders, anything worth remembering.
                </p>
            </div>

            <div className="card-body border-b border-paper-border-soft">
                <Textarea
                    value={composerText}
                    onChange={(e) => setComposerText(e.target.value)}
                    placeholder="Add a note…"
                    rows={2}
                    maxLength={5000}
                />
                <div className="mt-3 flex justify-end">
                    <Button
                        size="sm"
                        onClick={() => addNote.mutate()}
                        disabled={!composerText.trim() || addNote.isPending}
                        loading={addNote.isPending}
                    >
                        <MessageSquare className="h-3.5 w-3.5" />
                        Add note
                    </Button>
                </div>
            </div>

            {isLoading ? (
                <div className="card-body">
                    <p className="text-sm text-ink-subtle text-center py-4">Loading…</p>
                </div>
            ) : notes.length === 0 ? (
                <div className="card-body">
                    <p className="text-sm text-ink-subtle text-center py-2">No notes yet.</p>
                </div>
            ) : (
                <ul className="divide-y divide-paper-border-soft">
                    {notes.map((note) => (
                        <li key={note.id} className="px-6 py-4 flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                                <p className="text-sm text-ink whitespace-pre-wrap break-words">{note.note_text}</p>
                                <p className="mt-1.5 text-xs text-ink-subtle">
                                    {note.author_name} · {formatDateTime(note.created_at)}
                                </p>
                            </div>
                            {isOwner && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (window.confirm('Delete this note? This is audit-logged but cannot be undone.')) {
                                            deleteNote.mutate(note.id);
                                        }
                                    }}
                                    className="text-ink-muted hover:text-status-danger transition-colors flex-shrink-0 mt-0.5"
                                    aria-label="Delete note"
                                    disabled={deleteNote.isPending}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

// ============================================================================
// Single document row
// ============================================================================
function DocumentRow({ label, requiresExpiry, document: doc, isAdmin, waived, onWaive, onUpload, onPreview, isAdditional }) {
    const expiryStatus = doc && doc.expires_at ? computeExpiryStatus(doc.expires_at) : null;
        const clickable = Boolean(doc && onPreview);
    const isApproved = !doc?.approval_status || doc.approval_status === 'approved';

    return (
        <div
            className={
                'px-6 py-4 flex items-start justify-between gap-4 transition-colors ' +
                (clickable ? 'cursor-pointer hover:bg-paper-hover/50' : '')
            }
            onClick={clickable ? onPreview : undefined}
        >
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-ink">{label}</span>
                    {!doc && !isAdditional && (
                        waived
                            ? <StatusPill status="neutral">Not required</StatusPill>
                            : <StatusPill status="danger">Missing</StatusPill>
                    )}
                                        {doc?.approval_status === 'pending' && (
                        <StatusPill status="warning">Awaiting approval</StatusPill>
                    )}
                    {doc?.approval_status === 'rejected' && (
                        <StatusPill status="danger">Rejected</StatusPill>
                    )}
                    {isApproved && expiryStatus && <StatusPill status={expiryStatus.variant}>{expiryStatus.label}</StatusPill>}
                    {isApproved && doc && !doc.expires_at && !requiresExpiry && !isAdditional && (
                        <StatusPill status="success">
                            <CheckCircle2 className="h-3 w-3" />
                            On file
                        </StatusPill>
                    )}
                    {doc && !doc.expires_at && isAdditional && (
                        <StatusPill status="neutral">
                            <FileText className="h-3 w-3" />
                            No expiry
                        </StatusPill>
                    )}
                </div>
                {doc && (
                    <div className="mt-1 text-xs text-ink-subtle">
                        {doc.file_name}
                        {doc.expires_at && ` · Expires ${formatDate(doc.expires_at)}`}
                        · Uploaded {formatDate(doc.uploaded_at)}
                    </div>
                )}
            </div>
           <div className="flex items-center gap-2 flex-shrink-0">
                {!doc && !isAdditional && onWaive && (
                    waived ? (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onWaive(false); }}
                            className="text-xs text-ink-subtle hover:text-ink underline decoration-dotted"
                            title="Mark this document as required again"
                        >
                            Mark required
                        </button>
                    ) : (
                        <Button
                            size="sm"
                            variant="secondary"
                            onClick={(e) => { e.stopPropagation(); onWaive(true); }}
                            title="Mark as not required — stops showing under Needs attention"
                        >
                            <Check className="h-3.5 w-3.5" />
                            Not required
                        </Button>
                    )
                )}
                {isAdmin && onUpload && (
                    <Button
                        size="sm"
                        variant="secondary"
                        onClick={(e) => {
                            e.stopPropagation();
                            onUpload();
                        }}
                    >
                        <Upload className="h-3.5 w-3.5" />
                        {doc ? 'Replace' : 'Upload'}
                    </Button>
                )}
            </div>
        </div>
    );
}

// ============================================================================
// Disable modal
// ============================================================================
function DisableModal({ vehicleId, vehicleDescription, open, onClose, onDisabled }) {
    const api = useApi();
    const [reason, setReason] = useState('');

    const mutation = useMutation({
        mutationFn: () => api.post(`/vehicles/${vehicleId}/disable`, { reason }),
        onSuccess: () => {
            toast.success('Vehicle disabled');
            onDisabled();
        },
        onError: (err) => toast.error(err.message || 'Could not disable vehicle'),
    });

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Disable vehicle?"
            description={`${vehicleDescription} will no longer be assignable to new jobs.`}
            size="md"
        >
            <Textarea
                label="Reason"
                required
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. sold, written off, long-term repair"
                rows={3}
            />
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button
                    variant="danger"
                    onClick={() => mutation.mutate()}
                    loading={mutation.isPending}
                    disabled={!reason.trim()}
                >
                    <Ban className="h-4 w-4" />
                    Disable
                </Button>
            </div>
        </Modal>
    );
}

// ============================================================================
// Enable modal
// ============================================================================
function EnableModal({ vehicleId, vehicleDescription, open, onClose, onEnabled }) {
    const api = useApi();
    const [reason, setReason] = useState('');

    const mutation = useMutation({
        mutationFn: () => api.post(`/vehicles/${vehicleId}/enable`, reason.trim() ? { reason: reason.trim() } : {}),
        onSuccess: () => {
            toast.success('Vehicle re-enabled');
            onEnabled();
        },
        onError: (err) => toast.error(err.message || 'Could not re-enable vehicle'),
    });

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Re-enable vehicle?"
            description={`${vehicleDescription} will become available for dispatch again.`}
            size="md"
        >
            <Textarea
                label="Reason (optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. repaired, returned from lease"
                rows={3}
                hint="Optional — helpful context for the audit log."
            />
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button
                    onClick={() => mutation.mutate()}
                    loading={mutation.isPending}
                >
                    <RotateCcw className="h-4 w-4" />
                    Re-enable
                </Button>
            </div>
        </Modal>
    );
}

// =============================================================================
// EditableExpiry — inline editable date within the doc preview metadata strip
// =============================================================================
function EditableExpiry({ doc, ownerType, ownerId, requiresExpiry, isOwner }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(
        doc.expires_at ? new Date(doc.expires_at).toISOString().slice(0, 10) : ''
    );
    const [savedOverride, setSavedOverride] = useState(null);

    const mutation = useMutation({
        mutationFn: () => api.patch(`/documents/${doc.id}`, {
            expires_at: value && value.trim() !== '' ? value : null,
        }),
       onSuccess: () => {
    setSavedOverride(value && value.trim() !== '' ? value : '');
    // The documents list lives in its own query, keyed as ['documents', ownerType, ownerId].
// Invalidate both that one AND the parent entity (in case anything reads from it).
queryClient.refetchQueries({ queryKey: ['documents', ownerType, ownerId] });
queryClient.invalidateQueries({ queryKey: [ownerType, ownerId] });
queryClient.invalidateQueries({ queryKey: ['document-download-url', doc.id] });
    toast.success('Expiry updated');
    setEditing(false);
},
        onError: (err) => {
            toast.error(err?.message || 'Could not update expiry');
        },
    });

    // Use the just-saved value if we have one, otherwise the prop value.
// This bridges the gap between save-success and the parent refetch.
const displayedExpiry = savedOverride !== null ? savedOverride : doc.expires_at;

if (!editing) {
    if (!displayedExpiry) {
            // No expiry set — show "Set expiry" link only for owner
            if (!isOwner) return null;
            return (
                <button
                    type="button"
                    onClick={() => { setValue(''); setEditing(true); }}
                    className="text-ink-subtle hover:text-ink underline decoration-dotted"
                >
                    Set expiry
                </button>
            );
        }
        return (
            <span className="inline-flex items-center gap-1.5">
                <span>Expires {formatDate(displayedExpiry)}</span>
                {isOwner && (
                    <button
                        type="button"
                        onClick={() => {
                            setValue(new Date(displayedExpiry).toISOString().slice(0, 10));
                            setEditing(true);
                        }}
                        className="text-ink-subtle hover:text-ink p-0.5 rounded"
                        title="Edit expiry"
                        aria-label="Edit expiry"
                    >
                        <Pencil className="h-3 w-3" />
                    </button>
                )}
            </span>
        );
    }

    // Edit mode — small inline form: date input + Save + Cancel
    const canSave = !mutation.isPending && (value !== '' || !requiresExpiry);

    return (
        <span className="inline-flex items-center gap-1.5">
            <input
                type="date"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="px-1.5 py-0.5 text-xs border border-paper-border rounded focus:outline-none focus:ring-1 focus:ring-ink/30"
                disabled={mutation.isPending}
                autoFocus
            />
            <button
                type="button"
                onClick={() => mutation.mutate()}
                disabled={!canSave}
                className="p-0.5 rounded bg-ink text-white disabled:opacity-50"
                title="Save"
                aria-label="Save"
            >
                <Check className="h-3 w-3" />
            </button>
            <button
                type="button"
                onClick={() => setEditing(false)}
                disabled={mutation.isPending}
                className="p-0.5 rounded text-ink-subtle hover:text-ink"
                title="Cancel"
                aria-label="Cancel"
            >
                <X className="h-3 w-3" />
            </button>
        </span>
    );
}

// ============================================================================
// Document preview modal
// ============================================================================
function DocumentPreviewModal({ document: doc, open, onClose, ownerId, isOwner }) {
    const api = useApi();

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['document-download-url', doc.id],
        queryFn: () => api.get(`/documents/${doc.id}/download-url`),
        enabled: open,
        staleTime: 30_000,
    });

    const downloadUrl = data?.download_url;
    const mimeType = doc.mime_type || '';
    const isImage = mimeType.startsWith('image/');
    const isPdf = mimeType === 'application/pdf';

    function handleDownload() {
        if (!downloadUrl) return;
        const a = window.document.createElement('a');
        a.href = downloadUrl;
        a.download = doc.file_name;
        window.document.body.appendChild(a);
        a.click();
        window.document.body.removeChild(a);
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={doc.custom_label || formatDocType(doc.doc_type)}
            description={doc.file_name}
            size="lg"
        >
            <div className="space-y-4">
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-ink-subtle">
                    <span>Uploaded {formatDateTime(doc.uploaded_at)}</span>
                    <EditableExpiry
    doc={doc}
    ownerType="vehicle"
    ownerId={ownerId}
    requiresExpiry={['mot','insurance','phv_licence'].includes(doc.doc_type)}
    isOwner={isOwner}
/>
                    {doc.file_size && <span>{formatBytes(doc.file_size)}</span>}
                    <span className="font-mono">{mimeType || 'unknown type'}</span>
                </div>

                <div className="border border-paper-border rounded-md overflow-hidden bg-paper-hover/30" style={{ minHeight: '400px' }}>
                    {isLoading && (
                        <div className="flex items-center justify-center h-96 text-ink-subtle text-sm">
                            Loading preview…
                        </div>
                    )}
                    {isError && (
                        <div className="flex items-center justify-center h-96 text-status-danger text-sm">
                            Could not load preview: {error?.message || 'Unknown error'}
                        </div>
                    )}
                    {downloadUrl && isImage && (
                        <img
                            src={downloadUrl}
                            alt={doc.file_name}
                            className="w-full h-auto max-h-[70vh] object-contain bg-black/5"
                        />
                    )}
                    {downloadUrl && isPdf && (
                        <iframe
                            src={downloadUrl}
                            title={doc.file_name}
                            className="w-full h-[70vh] border-0"
                        />
                    )}
                    {downloadUrl && !isImage && !isPdf && (
                        <div className="flex flex-col items-center justify-center h-96 text-center p-6">
                            <FileText className="h-12 w-12 text-ink-subtle mb-3" />
                            <p className="text-sm text-ink">Preview not available for this file type.</p>
                            <p className="text-xs text-ink-subtle mt-1">Use the Download button below to view it.</p>
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Close</Button>
                <Button onClick={handleDownload} disabled={!downloadUrl}>
                    <Download className="h-4 w-4" />
                    Download
                </Button>
            </div>
        </Modal>
    );
}

// ============================================================================
// Helpers
// ============================================================================
function computeExpiryStatus(expiresAt) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiresAt);
    const daysOut = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

    if (daysOut < 0) return { variant: 'danger', label: 'Expired' };
    if (daysOut <= 7) return { variant: 'danger', label: `Expires in ${daysOut}d` };
    if (daysOut <= 14) return { variant: 'warning', label: `Expires in ${daysOut}d` };
    if (daysOut <= 30) return { variant: 'warning', label: `Expires ${formatDate(expiresAt)}` };
    return { variant: 'success', label: `Valid until ${formatDate(expiresAt)}` };
}

function formatDate(d) {
    if (!d) return null;
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(d) {
    if (!d) return null;
    return new Date(d).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

function formatDocType(type) {
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

function formatBytes(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
